import React from 'react';

const images = [
  { src: '/api/faces_previews/face_14_no_logo_preview.png', title: 'Aurora Pulse', tag: 'Dynamic Face' },
  { src: '/api/faces_previews/face_5_no_logo_preview.png', title: 'Minimal Glow', tag: 'Wallpaper Pack' },
  { src: '/api/faces_previews/face_8_no_logo_preview.png', title: 'Orbit Widgets', tag: 'Widget Stack' },
  { src: '/api/faces_previews/face_10_no_logo_preview.png', title: 'Sunrise Walk', tag: 'Lifestyle Face' },
  { src: '/api/faces_previews/face_11_no_logo_preview.png', title: 'Ticker Tape', tag: 'Data Widgets' },
  { src: '/api/faces_previews/face_12_no_logo_preview.png', title: 'Retro Grid', tag: 'Sci-fi Pack' },
  { src: '/api/faces_previews/face_13_no_logo_preview.png', title: 'Desert Bloom', tag: 'Seasonal' },
  { src: '/api/faces_previews/face_17_no_logo_preview.png', title: 'Night Mirage', tag: 'Always-On' },
];

// Duplicate array for infinite marquee illusion
const row1 = [...images, ...images];
const row2 = [...images.slice(4), ...images.slice(0, 4), ...images.slice(4), ...images.slice(0, 4)];

const GalleryCard: React.FC<{ image: typeof images[0]; size?: 'normal' | 'tall' }> = ({ image, size = 'normal' }) => (
  <div
    className="relative overflow-hidden rounded-3xl flex-shrink-0 hover-lift group cursor-pointer"
    style={{
      width: 190,
      height: size === 'tall' ? 240 : 210,
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    }}
  >
    <img
      src={image.src}
      alt={image.title}
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    {/* Overlay on hover */}
    <div
      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4"
    >
      <span className="text-[10px] uppercase tracking-widest text-[#68C1AE] font-bold">{image.tag}</span>
      <p className="text-sm font-bold text-white mt-1">{image.title}</p>
    </div>
    {/* Always visible subtle gradient */}
    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
    <div className="absolute bottom-3 left-4 right-4 pointer-events-none group-hover:opacity-0 transition-opacity duration-200">
      <span className="text-[10px] text-white/50 uppercase tracking-widest">{image.tag}</span>
    </div>
  </div>
);

const Gallery: React.FC = () => {
  return (
    <section
      id="gallery"
      className="py-28 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #080C0B 0%, #0A1210 50%, #080C0B 100%)',
      }}
    >
      <div className="container mx-auto mb-12 text-center">
        <span className="section-badge mb-4 inline-flex">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#68C1AE]" />
          This week's library
        </span>
        <h2 className="mt-4 text-4xl md:text-5xl font-extrabold text-white tracking-tight" style={{ letterSpacing: '-0.03em' }}>
          Hundreds of faces.<br />
          <span className="gradient-text">Zero compromise.</span>
        </h2>
        <p className="mt-4 text-lg text-white/45 max-w-xl mx-auto">
          Swipe through curated watch faces and wallpaper bundles that match your focus, schedule, and mood.
        </p>
      </div>

      {/* Marquee row 1 — left */}
      <div className="relative mb-4" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%)' }}>
        <div className="flex gap-4 animate-marquee" style={{ width: 'max-content' }}>
          {row1.map((img, i) => <GalleryCard key={i} image={img} />)}
        </div>
      </div>

      {/* Marquee row 2 — right */}
      <div className="relative" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%)' }}>
        <div className="flex gap-4 animate-marquee-reverse" style={{ width: 'max-content' }}>
          {row2.map((img, i) => <GalleryCard key={i} image={img} size="tall" />)}
        </div>
      </div>

      {/* CTA */}
      <div className="container mx-auto mt-14 text-center">
        <a
          href="https://apps.apple.com/us/app/watchi-watch-faces-gallery/id6753596004"
          className="btn-primary inline-flex"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          Unlock Full Gallery — Free
        </a>
        <p className="mt-3 text-sm text-white/30">Available on the App Store · Free to download</p>
      </div>
    </section>
  );
};

export default Gallery;
